/**
 * Latency-budget contract — PRODUCT DECISION D2.
 *
 * Glass-to-glass latency above the budget is a bug, not a slowdown. This module is the pure,
 * shared definition of that budget and the windowed verdict over a stream of samples. The
 * stateful per-peer wrapper lives in ./latencyBudgetMonitor; the absolute budget here is
 * deliberate — the difference-based congestion ladder stays for *entering* recovery, but
 * this budget governs *unacceptable* latency.
 *
 * WHAT THE BUDGET MAY ACT ON. A reading over budget is `floor + excess`: the floor is
 * propagation plus the fixed pipeline, which no encoder setting can change; the excess is
 * queueing or a decoder falling behind, which a cut can drain. The budget therefore only
 * ACTS when the excess is what put the player over it (`over-budget-fixable`). A link that is
 * over budget by its floor alone (`over-budget-floor`) is a guest far from the host, not a
 * faulty link: it is reported, never cut, and never raises a sticky floor — the sticky
 * downgrade exists for links that have proven themselves faulty (a moving mobile connection,
 * where consistent latency matters more than peak quality), and distance is not fault.
 *
 * ONE EXCEPTION TO "THE FLOOR IS DISTANCE". The floor is a window minimum, and a receive-side
 * buffer that stands for the whole window is absorbed into it. `LatencyBudgetSample.
 * standingLatencyKind` carries the standing-latency detector's verdict so the monitor can tell
 * a `route` floor (distance) from a `standing-buffer` floor (fixable, acted on by drain).
 *
 * Without a calibrated sample there is no verdict at all: the candidate-pair RTT is a
 * two-way, network-only number and comparing it to a one-way glass-to-glass budget is a
 * unit error, not a fallback.
 *
 * Browser-free and Node/Jest-safe: constants, types, and pure functions only.
 */

import type { LatencyFloorKind } from './standingLatency/standingLatencyContracts';

/** PRODUCT DECISION D2. Glass-to-glass latency above this (ms) is a bug. */
export const LATENCY_BUDGET_GLASS_TO_GLASS_MS = 100;

/** Sustained violation must hold this long (ms) before it is declared — not a blip. */
export const LATENCY_BUDGET_VIOLATION_HOLD_MS = 1_500;

/** Window of recent samples (ms) the budget is evaluated over. */
export const LATENCY_BUDGET_WINDOW_MS = 5_000;

/**
 * UNVALIDATED ESTIMATE. The smallest excess above a link's own floor worth acting on.
 * Calibrated readings are quantised by frame timing — the floor is the window minimum, so a
 * perfectly clean 60 fps link reads up to a frame interval (~17 ms) of "excess" from
 * quantisation alone — and below this a cut cannot buy back a meaningful amount of latency.
 * Calibrate against the `[CalibratedLatency]` lines in webrtc-debug.ndjson.
 */
export const LATENCY_BUDGET_MIN_FIXABLE_EXCESS_MS = 25;

export type LatencyBudgetSample = {
  atMs: number;
  /** Calibrated glass-to-glass latency, ms. Undefined when not measured this tick. */
  calibratedMs?: number;
  /** This link's calibrated latency floor (window minimum), ms. Undefined while untrusted. */
  floorMs?: number;
  /**
   * What the floor is made of, from the standing-latency detector: `route` (distance, never
   * acted on) or `standing-buffer` (frames standing on the receive side, acted on). Undefined
   * when the detector had no verdict this tick; the monitor keeps the last kind it was told.
   */
  standingLatencyKind?: LatencyFloorKind;
};

/** What one calibrated reading says about the budget, once split into floor and excess. */
export type LatencyBudgetClass =
  /** No calibrated reading, or no trusted floor to attribute it with. Not evidence. */
  | 'unmeasured'
  /** At or under the budget. */
  | 'within'
  /** Over budget, but the excess above the floor is below the fixable minimum: distance. */
  | 'over-budget-floor'
  /** Over budget AND the excess is large enough that a cut can buy it back: act. */
  | 'over-budget-fixable';

export type LatencyBudgetVerdict = 'within' | 'breach-pending' | 'breach';

/**
 * Classify one reading. Pure; the budget and fixable minimum are parameters so the monitor
 * and the climb gate judge with the same numbers.
 */
export function classifyLatencyBudget(
  sample: Pick<LatencyBudgetSample, 'calibratedMs' | 'floorMs'>,
  budgetMs: number = LATENCY_BUDGET_GLASS_TO_GLASS_MS,
  minFixableExcessMs: number = LATENCY_BUDGET_MIN_FIXABLE_EXCESS_MS,
): LatencyBudgetClass {
  if (!isFinite(sample.calibratedMs) || !isFinite(sample.floorMs)) return 'unmeasured';
  if (sample.calibratedMs <= budgetMs) return 'within';
  const excessMs = Math.max(0, sample.calibratedMs - sample.floorMs);
  return excessMs >= minFixableExcessMs ? 'over-budget-fixable' : 'over-budget-floor';
}

/**
 * Evaluate the latency budget over a recent sample window.
 *
 * Only `over-budget-fixable` samples build a run; `unmeasured` samples carry no evidence
 * and are ignored (they neither extend nor break a run); any other class resets it. The
 * verdict is `breach` once the run has been held for `holdMs` — measured between the first
 * and the LATEST fixable sample, never against the wall clock, so silence after one bad
 * reading is not a breach (missing evidence never locks). With zero measured samples there
 * is no evidence of violation, so the verdict is `within`.
 *
 * The function is pure: callers may pass the full recent window each tick.
 */
export function evaluateLatencyBudget(
  samples: LatencyBudgetSample[],
  budgetMs: number = LATENCY_BUDGET_GLASS_TO_GLASS_MS,
  holdMs: number = LATENCY_BUDGET_VIOLATION_HOLD_MS,
  minFixableExcessMs: number = LATENCY_BUDGET_MIN_FIXABLE_EXCESS_MS,
): LatencyBudgetVerdict {
  const measured = samples
    .map((s) => ({ atMs: s.atMs, klass: classifyLatencyBudget(s, budgetMs, minFixableExcessMs) }))
    .filter((s) => s.klass !== 'unmeasured')
    .sort((a, b) => a.atMs - b.atMs);
  if (measured.length === 0) return 'within';

  // Walk back from the most recent sample to find the current continuous fixable run.
  let runStartMs: number | null = null;
  for (let i = measured.length - 1; i >= 0; i--) {
    if (measured[i].klass !== 'over-budget-fixable') break;
    runStartMs = measured[i].atMs;
  }
  if (runStartMs === null) return 'within';

  const heldMs = measured[measured.length - 1].atMs - runStartMs;
  return heldMs >= holdMs ? 'breach' : 'breach-pending';
}

function isFinite(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
